/**
 * SSE streaming client for the Agent Builder Chat API.
 *
 * Both public methods are async generators that yield `StreamingEvent`
 * objects as they arrive from the server.
 *
 * Authentication uses an Evo Access Button token (not the regular OIDC
 * access token) — the Agent Builder API requires this specific token type.
 */

import { getAgentBuilderApiUrl } from '@/lib/agent-config';
import { getButtonToken } from '@/services/evo-token.service';
import type { StreamingEvent } from '@/types/streaming.types';

// ============================================================================
// Instance ID injection
// ============================================================================

let _instanceId: string | null = null;

/**
 * Called by AIAssistantProvider on mount to inject the instance ID.
 */
export function setStreamAuthProvider(
  _getAccessToken: () => Promise<string | null>,
  instanceId: string | undefined,
): void {
  _instanceId = instanceId ?? null;
}

// ============================================================================
// Request types
// ============================================================================

/** Execution mode sent to the Agent Builder API. */
export type AgentExecutionMode = 'deep' | 'fast';

const DEFAULT_EXECUTION_MODE: AgentExecutionMode = 'deep';

export interface StartConversationStreamRequest {
  readonly agent_id: string;
  readonly message: string;
  readonly mode?: AgentExecutionMode;
}

export interface SendMessageStreamRequest {
  readonly agent_id: string;
  readonly message: string;
  readonly mode?: AgentExecutionMode;
}

// ============================================================================
// Streaming client
// ============================================================================

/**
 * Build request headers for the Agent Builder API.
 *
 * Uses the Evo Access Button token for authentication and sends
 * `X-Data-Instance-Id` so the Agent Builder knows which data tenant
 * the request originates from.
 */
async function getStreamingHeaders(): Promise<HeadersInit> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };

  const bearerToken = await getButtonToken();
  if (bearerToken) {
    headers['Authorization'] = `Bearer ${bearerToken}`;
  }

  if (_instanceId) {
    headers['X-Data-Instance-Id'] = _instanceId;
  }

  return headers;
}

/**
 * Start a new conversation with SSE streaming.
 * Yields streaming events as they arrive from the server.
 */
export async function* startConversationStreaming(
  data: StartConversationStreamRequest,
  signal?: AbortSignal,
): AsyncGenerator<StreamingEvent> {
  const baseUrl = getAgentBuilderApiUrl();
  const headers = await getStreamingHeaders();

  const response = await fetch(`${baseUrl}/api/Chat/conversations/stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ mode: DEFAULT_EXECUTION_MODE, ...data }),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `Failed to start streaming conversation: ${response.status}`);
  }

  if (!response.body) {
    throw new Error('Response body is not available for streaming');
  }

  yield* parseSSEStream(response.body);
}

/**
 * Continue an existing conversation with SSE streaming.
 * Yields streaming events as they arrive from the server.
 */
export async function* sendMessageStreaming(
  conversationId: string,
  data: SendMessageStreamRequest,
  signal?: AbortSignal,
): AsyncGenerator<StreamingEvent> {
  const baseUrl = getAgentBuilderApiUrl();
  const headers = await getStreamingHeaders();

  const response = await fetch(`${baseUrl}/api/Chat/conversations/${conversationId}/messages/stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ mode: DEFAULT_EXECUTION_MODE, ...data }),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `Failed to send streaming message: ${response.status}`);
  }

  if (!response.body) {
    throw new Error('Response body is not available for streaming');
  }

  yield* parseSSEStream(response.body);
}

// ============================================================================
// SSE parsing helpers
// ============================================================================

async function* parseSSEStream(body: ReadableStream<Uint8Array>): AsyncGenerator<StreamingEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();

      if (done) {
        yield* flushRemainingBuffer(buffer);
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() ?? '';

      yield* parseEventChunks(chunks);
    }
  } finally {
    reader.releaseLock();
  }
}

function* flushRemainingBuffer(buffer: string): Generator<StreamingEvent> {
  if (!buffer.trim()) return;

  const event = parseSSEEvent(buffer);
  if (event) {
    yield event;
  }
}

function* parseEventChunks(chunks: string[]): Generator<StreamingEvent> {
  for (const chunk of chunks) {
    if (!chunk.trim()) continue;

    const event = parseSSEEvent(chunk);
    if (event) {
      yield event;
    }
  }
}

function parseSSEEvent(eventText: string): StreamingEvent | null {
  const lines = eventText.split('\n');
  let data: string | null = null;

  for (const line of lines) {
    if (line.startsWith('data:')) {
      data = line.slice(5).trim();
    }
  }

  if (!data) {
    return null;
  }

  try {
    return JSON.parse(data) as StreamingEvent;
  } catch {
    return null;
  }
}
